import {
  Box,
  Card,
  Icon,
  Progress,
  SimpleGrid,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
} from '@chakra-ui/react';
import { MoveRight, TrendingDown, TrendingUp } from 'lucide-react';
import { useOfferScorecardsForOrgContext } from './OfferScorecardsForOrgContext';

export const OfferScorecardsMetrics = () => {
  const {
    metrics: { data: offerScorecardsMetrics, loading },
  } = useOfferScorecardsForOrgContext();

  if (loading) {
    return (
      <Progress
        isIndeterminate
        height="2px"
        size="xs"
        visibility={loading ? 'visible' : 'hidden'}
        width="100%"
      />
    );
  }

  if (!offerScorecardsMetrics) {
    return null;
  }

  const { created, shared, averageScore } = offerScorecardsMetrics;

  return (
    <Box overflowX="auto" width="100%">
      <SimpleGrid
        columns={{ base: 3, md: 3 }}
        minWidth={{ base: '600px', md: '1000px' }}
        spacing={{ base: 3, md: 6 }}
        width={{ base: 'max-content', md: '100%' }}
      >
        <MetricCard
          delta={created.delta}
          label="Created This Week"
          value={created.value}
        />
        <MetricCard
          delta={shared.delta}
          label="Shared This Week"
          value={shared.value}
        />
        <MetricCard
          delta={averageScore.delta}
          fractionDigits={1}
          label="Average Offer Score This Week"
          value={averageScore.value}
        />
      </SimpleGrid>
    </Box>
  );
};

function MetricCard({
  label,
  value,
  delta,
  fractionDigits = 0,
}: {
  label: string;
  value: number;
  delta: number;
  fractionDigits?: number;
}) {
  return (
    <Card
      alignItems="center"
      bg="whiteAlpha.50"
      display="flex"
      height={{ base: '180px', md: 'auto' }}
      p={{ base: 4, md: 8 }}
      width={{ base: '60vw', md: 'auto' }}
    >
      <Stat textAlign="center">
        <StatLabel
          alignItems="center"
          color="whiteAlpha.600"
          display="flex"
          fontSize="sm"
          height={{ base: '40px', md: '44px' }}
          justifyContent="center"
          letterSpacing="0.02em"
          mb={4}
          px={2}
          textAlign="center"
          textTransform="uppercase"
        >
          {label}
        </StatLabel>
        <StatNumber
          fontSize={{ base: '36px', md: '48px' }}
          fontWeight="medium"
          lineHeight="1"
        >
          {value.toFixed(fractionDigits)}
        </StatNumber>
        <OfferDraftsMetricsDelta
          delta={delta}
          deltaFractionDigits={fractionDigits}
        />
      </Stat>
    </Card>
  );
}

function OfferDraftsMetricsDelta({
  delta,
  deltaFractionDigits = 0,
}: {
  delta: number;
  deltaFractionDigits?: number;
}) {
  return (
    <StatHelpText
      alignItems="center"
      bg="whiteAlpha.100"
      borderRadius="md"
      color="whiteAlpha.600"
      display="inline-flex"
      fontSize="sm"
      maxWidth="100%"
      mt={4}
      overflow="hidden"
      px={3}
      py={2}
      whiteSpace="nowrap"
    >
      {delta > 0 ? (
        <Icon
          as={TrendingUp}
          color="green.500"
          flexShrink={0}
          mr={1}
          size={16}
        />
      ) : delta < 0 ? (
        <Icon
          as={TrendingDown}
          color="red.500"
          flexShrink={0}
          mr={1}
          size={16}
        />
      ) : (
        <Icon as={MoveRight} color="gray.500" flexShrink={0} mr={1} size={16} />
      )}
      {Math.abs(delta).toFixed(deltaFractionDigits)} from last week
    </StatHelpText>
  );
}
